import React, { useEffect, useMemo, useState } from "react";
import {
  DATE_RANGE_PRESETS,
  detectPresetFromRange,
  getPresetDateRange,
} from "../utils/rideReportDatePresets";
import { formatBreakdownDateRangeLabel } from "../utils/rideBreakdownComparison";

const inputClass =
  "w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white";

const inlineInputClass =
  "w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white";

const ReportDateRangeFilters = ({
  startDate = "",
  endDate = "",
  onStartDateChange,
  onEndDateChange,
  variant = "card",
}) => {
  const [dateRangePreset, setDateRangePreset] = useState("");

  const startYmd = String(startDate || "").trim();
  const endYmd = String(endDate || "").trim();
  const hasMainDateRange = Boolean(startYmd && endYmd);

  useEffect(() => {
    setDateRangePreset(detectPresetFromRange(startYmd, endYmd));
  }, [startYmd, endYmd]);

  const activeRangeLabel = useMemo(() => {
    if (!hasMainDateRange) return "";
    if (dateRangePreset) {
      const preset = DATE_RANGE_PRESETS.find((p) => p.value === dateRangePreset);
      return preset?.label || formatBreakdownDateRangeLabel(startYmd, endYmd);
    }
    return formatBreakdownDateRangeLabel(startYmd, endYmd);
  }, [hasMainDateRange, dateRangePreset, startYmd, endYmd]);

  const handlePresetChange = (preset) => {
    setDateRangePreset(preset);
    if (!preset) return;
    const range = getPresetDateRange(preset);
    if (!range) return;
    onStartDateChange?.(range.startDate);
    onEndDateChange?.(range.endDate);
  };

  const handleStartChange = (value) => {
    setDateRangePreset("");
    onStartDateChange?.(value);
  };

  const handleEndChange = (value) => {
    setDateRangePreset("");
    onEndDateChange?.(value);
  };

  const todayYmd = getPresetDateRange("today")?.endDate || "";
  const fieldClass = variant === "inline" ? inlineInputClass : inputClass;

  const fields = (
    <>
      <div className="min-w-0">
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Date Range
        </label>
        <select
          value={dateRangePreset}
          onChange={(e) => handlePresetChange(e.target.value)}
          className={fieldClass}
        >
          {DATE_RANGE_PRESETS.map((preset) => (
            <option key={preset.value || "custom"} value={preset.value}>
              {preset.label}
            </option>
          ))}
        </select>
        {variant === "inline" && hasMainDateRange && (
          <span className="inline-flex mt-1 items-center rounded-full bg-blue-100 px-2 py-0.5 text-xs font-medium text-blue-800">
            {activeRangeLabel}
          </span>
        )}
      </div>
      <div className="min-w-0">
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Start Date
        </label>
        <input
          type="date"
          value={startYmd}
          onChange={(e) => handleStartChange(e.target.value)}
          max={todayYmd || undefined}
          className={fieldClass}
        />
      </div>
      <div className="min-w-0">
        <label className="block text-sm font-medium text-gray-700 mb-2">
          End Date
        </label>
        <input
          type="date"
          value={endYmd}
          onChange={(e) => handleEndChange(e.target.value)}
          max={todayYmd || undefined}
          min={startYmd || undefined}
          className={fieldClass}
        />
      </div>
    </>
  );

  if (variant === "inline") {
    return fields;
  }

  return (
    <div
      className={`rounded-lg border p-4 transition-colors duration-200 ${
        hasMainDateRange
          ? "bg-blue-50/50 border-blue-200 ring-1 ring-blue-200"
          : "bg-gray-50/80 border-gray-200"
      }`}
    >
      <div className="mb-3">
        <h4 className="text-sm font-semibold text-gray-700">Date Range</h4>
        {hasMainDateRange && (
          <span className="inline-flex mt-1 items-center rounded-full bg-blue-100 px-2.5 py-0.5 text-xs font-medium text-blue-800">
            {activeRangeLabel}
          </span>
        )}
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {fields}
      </div>
    </div>
  );
};

export default ReportDateRangeFilters;
